import { useEffect, useState } from 'react'
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react'
import { IModalProps } from './types'
import styles from './styles.module.scss'
import { ButtonBase } from '../Button/ButtonBase'
import clsx from 'clsx'

const TRANSITION_DURATION = 300

const Modal = ({
  children,
  className,
  closeOnOutsideClick = true,
  hasCloseButton = true,
  isVisible = false,
  onChange,
  size = 'sm',
  slots,
  trigger,
  width = '800px',
  idToAppend,
  hasOverlay = true,
  hasAnimation = true,
  ...props
}: IModalProps) => {
  const [isOpen, setIsOpen] = useState(isVisible)

  // used to programmatically set open and close
  useEffect(() => {
    setIsOpen(isVisible)
  }, [isVisible])

  // used to catch any change event. EG: open, close and click outside
  useEffect(() => {
    onChange?.(isOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  })

  const click = useClick(context)
  const role = useRole(context)
  const dismiss = useDismiss(context, {
    enabled: closeOnOutsideClick,
    outsidePressEvent: 'mousedown',
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, role, dismiss])

  const headingId = useId()
  const descriptionId = useId()

  /**
   * @todo remove this when the slots.before and slots.after are removed
   */
  const header = slots?.header || slots?.before
  const footer = slots?.footer || slots?.after

  const { status } = useTransitionStatus(context, {
    duration: TRANSITION_DURATION,
  })

  return (
    <>
      {trigger && (
        <div className={`${styles.trigger}`} ref={refs.setReference} {...getReferenceProps()}>
          {trigger}
        </div>
      )}

      <FloatingPortal id={idToAppend}>
        {isOpen && (
          <FloatingOverlay
            className={clsx(styles.wrap, {
              [styles.withAnimation]: hasAnimation,
              [styles.withOverlay]: hasOverlay,
              [styles.entering]: status === 'open',
              [styles.leaving]: status === 'close',
            })}
            lockScroll
          >
            <FloatingFocusManager context={context}>
              <>
                <div
                  aria-describedby={descriptionId}
                  aria-labelledby={headingId}
                  className={clsx(
                    styles.modal,
                    styles[size],
                    {
                      [styles.withAnimation]: hasAnimation,
                      [styles.entering]: status === 'open',
                      [styles.leaving]: status === 'close',
                    },
                    'mui-modal',
                    className,
                  )}
                  data-testid="mui-modal"
                  ref={refs.setFloating}
                  style={{ maxWidth: width }}
                  {...props}
                  {...getFloatingProps()}
                >
                  {header && (
                    <header className={clsx('mui-modal-header', styles.header)}>
                      {header}
                      {hasCloseButton && (
                        <ButtonBase
                          className={`${styles.close}`}
                          ref={refs.setReference}
                          {...getReferenceProps()}
                        >
                          <CloseIcon />
                        </ButtonBase>
                      )}
                    </header>
                  )}
                  <div className={clsx('mui-modal-body', styles.body)}>{children}</div>
                  {footer && (
                    <footer className={clsx('mui-modal-footer', styles.footer)}>{footer}</footer>
                  )}
                </div>
              </>
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
    </>
  )
}

export default Modal

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 384 512">
    <path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z" />
  </svg>
)
